import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { SERVICES, IMAGES } from "../data/site";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

const ServiceSection = ({ service, flip }) => (
  <section
    id={service.id}
    data-testid={`service-section-${service.id}`}
    className="scroll-mt-24 border-b border-white/10 py-16 sm:py-24"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${flip ? "" : ""}`}>
        <Reveal className={flip ? "lg:order-2" : ""}>
          <div className="group relative">
            <div className="clip-frame relative overflow-hidden border border-white/10">
              <img
                src={service.image}
                alt={service.alt}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />
            </div>
            <span className="absolute -bottom-3 left-6 border border-blueprint/30 bg-obsidian px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-blueprint">
              SEC {service.num} // {service.tag}
            </span>
          </div>
        </Reveal>
        <div className={flip ? "lg:order-1" : ""}>
          <Reveal>
            <span className="font-mono text-sm text-brand-light">/{service.num}</span>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
              {service.title}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-400">{service.blurb}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="mt-8 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
              {service.specs.map((spec) => (
                <li
                  key={spec}
                  className="flex items-start gap-2.5 bg-obsidian px-4 py-3.5 text-sm text-slate-300"
                >
                  <Plus className="mt-0.5 h-4 w-4 shrink-0 text-brand-light" />
                  {spec}
                </li>
              ))}
            </ul>
            <Link
              to={`/request-bid?service=${encodeURIComponent(service.title)}`}
              data-testid={`service-cta-${service.id}`}
              className="group mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-blueprint hover:text-white"
            >
              Request this capability
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default function Capabilities() {
  return (
    <>
      <PageHero
        testId="capabilities-hero"
        eyebrow="Capability Matrix // 08 Disciplines"
        lines={["Equipment.", "People.", "Know-how."]}
        sub="From horizontal directional drilling and trenching to conduit, fiber and cable placement, manholes, and turnkey OSP buildouts — LTEC handles complex underground projects across California, Nevada & Arizona."
        image={IMAGES.capabilitiesHero}
      />
      <Marquee />
      <div>
        {SERVICES.map((s, i) => (
          <ServiceSection key={s.id} service={s} flip={i % 2 === 1} />
        ))}
      </div>
      <section className="py-20 sm:py-28" data-testid="capabilities-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow className="justify-center">Any Size Project</Eyebrow>
            <h2 className="mx-auto mt-6 max-w-3xl font-display text-4xl sm:text-6xl font-black uppercase leading-[0.95] tracking-tight text-white">
              Not sure which method fits your crossing?
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base text-slate-400">
              Send the plans. We'll tell you honestly whether HDD, auger boring, or open
              trench is the right call.
            </p>
            <Link
              to="/request-bid"
              data-testid="capabilities-cta-contact"
              className="mt-10 inline-flex items-center gap-2 bg-brand px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-light"
            >
              Talk to Estimating <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
